// The public landing — flat brutalist receipt idiom FUSED with the
// industrial-brutalist taste (Swiss Industrial Print in light, Tactical Telemetry
// CRT in dark): dashed rules, square corners, one amber, NO gradient/shadow/sheen,
// tokens only — but the TYPOGRAPHY now carries the structure. The signature move is
// a macro architectural headline (SUPERVISION, massive, uppercase, heavy Space
// Grotesk, tight negative tracking, compressed leading) that owns the first screen
// like blacksmith.sh's BLACKSMITH / Floria's "Botanic Architecture.", with the
// metadata as tactical telemetry: tiny uppercase mono, generous tracking, ASCII
// brackets [ LABEL ], a masthead rev/coordinate line, beat-index numerals, and a
// few blueprint crosshairs/registration ticks at grid intersections.
//
// Two archetypes, one per substrate (never mixed within a mode):
//   LIGHT = Swiss Industrial Print — matte paper (--waytide-comp-receipt-surface),
//           carbon ink, heavy sans macro, crisp.
//   DARK  = Tactical Telemetry CRT — near-black carbon, mono dominance; the ASCII
//           brackets + crosshairs read strongest here.
// A CLEARLY VISIBLE 90s-newsprint paper grain (~15% light / ~12% dark, a coarse
// felt fibre) gives both the cheap-printed-paper feel of a newspaper coupon page —
// obvious tooth in the stock, while the ink stays crisp/legible.
//
// The fixed four-beat bones are KEPT — Masthead → Hero → What it caught → Trust →
// Close, the interactive transmission SCHEMATIC + the watching ⇄ caught toggle, the
// catch (summary chips + diff + email card), the mono/sans split by ROLE. Copy and
// space are TERSE: fewest direct words, tight bands — every sentence earns its
// brain cycles or goes.
//
// COPY = problem-first AIDA, told in the fewest words that stay true. ATTENTION —
// the headline is the gain in six words; the deck is the problem + the turn in
// three short sentences. INTEREST — the live schematic (five operators: Supervisor
// and Signal are the doors; Engineering, Design, Product arbitrate their domains) +
// one real catch, agent never stopped. DESIRE — the trust trio (one-write
// read-only, your runtime, zero retention). ACTION — sign in. Claims are TRUE only
// — no latency promises, no predictions.
//
// ONE accent = our amber --waytide-comp-receipt-stamp: exactly one amber in the
// default SSR render — the catch's "needs you" stamp on the steer card. Operator
// hues: supervisor violet, the arbiter tier teal, signal blue; the diff carries
// glyph-AND-hue add/del. The schematic's caught-state amber only appears on click.
//
// Token-pure: every value is a --waytide-comp-receipt-* / --waytide-sys-* var or a
// color-mix of one; any local custom property is --rcpt-* (the old-survivor build
// gate forbids the legacy double-dash-wt prefix). No raw hex, no gradient, no
// box-shadow but `none`, radius 0. The supervisor hue is
// --waytide-sys-watch-supervisor (the dedicated Supervisor accent token).

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  DEFAULT_INSTALLERS,
  platformLabel,
  readUserAgent,
  selectPreferredInstaller,
} from "./installers";
import "./landing.css";
import { ThemeCycle } from "../theme/ThemeCycle";
import { useOrchestratorAuth } from "../auth/OrchestratorSignIn";

// The receipt date line — a fixed, deterministic stamp. Lowercase day · month.
const PRINTED_ON = "05 jul";

// The app version, shown under the wordmark; the env tag rides beside it on
// staging/local only. Keep in step with package.json.
const VERSION = "0.2.0";

// detectEnv — SSR-safe (the page is SSR-rendered in tests). null in production
// (show only the version); "staging" / "local" elsewhere.
function detectEnv(): "staging" | "local" | null {
  if (typeof window === "undefined") return null;
  const h = window.location.hostname;
  if (h === "localhost" || h === "127.0.0.1" || h.endsWith(".local")) return "local";
  if (h.startsWith("staging") || h.includes("staging")) return "staging";
  return null;
}

/** The auth call-to-action. Signed out → "sign in" → /signin (the provider
 * choice — Google or Apple — lives on /signin, so the CTA stays terse). Signed
 * in → "dashboard" → /dashboard, so "/" stays the public landing for a logged-in
 * visitor — only the CTA's destination + label change. SSR/tests have no kept
 * token, so they render the signed-out form (the asserted copy + testids). */
function AuthCta({
  className,
  testid,
  inLabel,
  outLabel,
}: {
  className: string;
  testid?: string;
  inLabel: string;
  outLabel: string;
}) {
  const { signedIn } = useOrchestratorAuth();
  return (
    <Link
      to={signedIn ? "/dashboard" : "/signin"}
      className={className}
      data-testid={testid}
    >
      {signedIn ? inLabel : outLabel} <span aria-hidden>&rarr;</span>
    </Link>
  );
}

// The page's one mode. `watching` = calm (the floor); `caught` = an alert is
// live. The single control flips the schematic between the two.
type Mode = "watching" | "caught";

// ── The transmission ────────────────────────────────────────────────────────
// Session Agent → Supervisor → the arbiters (Engineering · Design · Product) →
// Signal → Brain. Two doors: only Supervisor touches the agent, only Signal
// speaks to you. Each row carries an operator hue (or none), a one-line role on
// hover, and a tiny mono status pill that differs between calm and alert. `lit`
// marks the rows the alert lights in `caught`.
type CogHue = "supervisor" | "arbiter" | "signal" | null;
type Cog = {
  id: string;
  name: string;
  hue: CogHue;
  role: string;
  calm: string;
  caught: string;
  litInCaught: boolean;
};

// Transmission order. The three arbiters share one row — one tier, one hue —
// so the chain stays five rows and the page stays tight.
const COGS: readonly Cog[] = [
  {
    id: "agent",
    name: "Session Agent",
    hue: null,
    role: "your coding agent, on your own runtime",
    calm: "running",
    caught: "running",
    litInCaught: false,
  },
  {
    id: "supervisor",
    name: "Supervisor",
    hue: "supervisor",
    role: "the only door to the agent; catches the edit outside the plan",
    calm: "watching",
    caught: "caught",
    litInCaught: true,
  },
  {
    id: "arbiters",
    name: "Engineering · Design · Product",
    hue: "arbiter",
    role: "one arbiter per domain — engineering, design, product — each owns its call",
    calm: "clear",
    caught: "called",
    litInCaught: true,
  },
  {
    id: "signal",
    name: "Signal",
    hue: "signal",
    role: "the only voice to you; one tap, provenance kept",
    calm: "quiet",
    caught: "sent",
    litInCaught: true,
  },
  {
    id: "brain",
    name: "Brain",
    hue: null,
    role: "remembers across runs; the next one starts knowing it",
    calm: "synced",
    caught: "synced",
    litInCaught: false,
  },
] as const;

// The catch summary — three stages, one mono record line, each chip carrying
// its operator hue (never amber). The classes
// wt-receipt-catch__row--{supervisor,engineering,signal} ride the chips so the
// hue marks + the tests survive.
const CATCH_CHIPS = [
  { op: "supervisor" as const, n: "①", name: "Supervisor", verb: "caught" },
  { op: "engineering" as const, n: "②", name: "Engineering", verb: "called" },
  { op: "signal" as const, n: "③", name: "Signal", verb: "sent" },
] as const;

// The trust block — three flat lines, sans (a human wrote them). key + value;
// none of it the reserved amber.
const TRUST = [
  { k: "read-only", v: "One write — the session hook. Everything else is read-only until you grant it." },
  { k: "your runtime", v: "Your Claude Code, your Codex, your machine. Never your keys." },
  { k: "zero retention", v: "No logging, no training. The brain is encrypted; safe providers only." },
] as const;


export function LandingPage() {
  // The single source of truth the schematic reacts to.
  const [mode, setMode] = useState<Mode>("watching");

  return (
    <div className="wt-receipt-page" data-mode={mode}>
      {/* The paper/newsprint grain — a CLEARLY VISIBLE coarse fibrous tooth (one
          desaturated, alpha-opaque fractalNoise SVG), aria-hidden, pointer-events-
          none. Multiplied into the newsprint in light (~15%), screened over the
          charcoal in dark (~12%), coarsened (baseFrequency 0.7) so the speckle is
          plainly perceptible as cheap 90s-newsprint stock — the one allowed use of
          opacity on the page — while the ink stays crisp. */}
      <div className="wt-receipt-grain" aria-hidden />

      <a href="#main" className="wt-receipt-skip">
        Skip to content
      </a>

      <main id="main" className="wt-receipt">
        <Masthead />

        <TearRule heavy />

        <HeroBand mode={mode} setMode={setMode} />

        <TearRule heavy />

        <CatchBand />

        <TearRule heavy />

        <TrustBand />

        <TearRule heavy />

        <CloseBand />

        <p className="wt-receipt-footline">
          read-only by default · your code never leaves your machine
        </p>
      </main>
    </div>
  );
}

/** A tactical eyebrow — the skill's micro-data register: a beat index (mono, dim)
 * + an ASCII-bracketed uppercase mono label `[ LABEL ]`. The bracket glyphs are
 * aria-hidden so the bracket texture never reaches the accessibility tree; the
 * label text stays readable. Small + dim + wide-tracked. */
function Eyebrow({ index, label }: { index?: string; label: string }) {
  return (
    <span className="wt-eyebrow">
      {index ? (
        <span className="wt-eyebrow__idx" aria-hidden>
          {index}
        </span>
      ) : null}
      <span className="wt-eyebrow__tag">
        <span className="wt-eyebrow__bracket" aria-hidden>
          [
        </span>
        <span className="wt-eyebrow__label">{label}</span>
        <span className="wt-eyebrow__bracket" aria-hidden>
          ]
        </span>
      </span>
    </span>
  );
}

/** Four corner registration ticks — short L-shaped rules at the corners of a
 * focal card (the "declassified blueprint" texture). Purely decorative. */
function CornerTicks() {
  return (
    <span className="wt-ticks" aria-hidden>
      <span className="wt-ticks__c wt-ticks__c--tl" />
      <span className="wt-ticks__c wt-ticks__c--tr" />
      <span className="wt-ticks__c wt-ticks__c--bl" />
      <span className="wt-ticks__c wt-ticks__c--br" />
    </span>
  );
}

/** The masthead — a full-width ledger header in ruled cells: the wordmark (with a
 * ™ industrial mark) + the supervision tag (sans) and a tactical telemetry line
 * (mono rev/env readout, dim, wide); then the appearance toggle + the quiet
 * sign-in. The redundant version/env meta cell was removed (it duplicated the
 * under-logo telemetry). Vertical dashed rules divide the cells. */
function Masthead() {
  const env = detectEnv();
  // The telemetry rev line — a sparse, TRUTHFUL machine readout (no fake
  // coordinates: the product shows you the truth). "UNIT /" was dropped
  // (meaningless): the line is now ● REV {VERSION} · {ENV}, env UPPERCASE on
  // staging/local, and just ● REV {VERSION} in production (env null). The
  // redundant top-right meta cell (the duplicate v{VERSION} + env chip) was
  // removed entirely — this telemetry line is the single rev/env readout.
  return (
    <header className="wt-masthead">
      <div className="wt-masthead__brand">
        <span className="wt-receipt-wordmark">
          WAYTIDE
          <span className="wt-receipt-wordmark__mark" aria-hidden>
            ™
          </span>
        </span>
        <span className="wt-masthead__tag">agent supervision · {PRINTED_ON}</span>
        <span className="wt-masthead__telemetry" aria-hidden>
          <span className="wt-masthead__live">●</span> REV {VERSION}
          {env ? ` · ${env.toUpperCase()}` : ""}
        </span>
      </div>
      <ThemeCycle />
      <AuthCta
        className="wt-masthead__signin"
        testid="landing-signin"
        outLabel="sign in"
        inLabel="dashboard"
      />
    </header>
  );
}

/** The hero — two columns, and it OWNS the first screen. LEFT: a tactical eyebrow,
 * the macro architectural headline (SUPERVISION massive + FOR YOUR CODING AGENTS a
 * step smaller, both uppercase, heavy sans), a plain sentence-case sans sub-line,
 * a FILLED primary CTA with a terse mono note. RIGHT (the one visual): the live
 * transmission SCHEMATIC, driven by the watching ⇄ caught toggle above it. A
 * vertical rule divides. */
function HeroBand({
  mode,
  setMode,
}: {
  mode: Mode;
  setMode: (m: Mode) => void;
}) {
  return (
    <section className="wt-hero" aria-labelledby="wt-receipt-lede">
      {/* The macro headline spans the hero's full width — the architectural block
          (like blacksmith's full-bleed BLACKSMITH) gets all the room it needs; the
          tactical eyebrow tags it. SUPERVISION is the heaviest line, the
          continuation a clear step smaller. The h1's aria-label is the readable
          phrase; the two visible spans are aria-hidden so AT hears one sentence. */}
      <div className="wt-hero__head">
        <Eyebrow index="00" label="CLAUDE CODE & CODEX" />
        <h1
          id="wt-receipt-lede"
          className="wt-receipt-headline"
          aria-label="Run ten agents. Get one message."
        >
          <span className="wt-receipt-headline__macro" aria-hidden>
            Run ten agents
          </span>
          <span className="wt-receipt-headline__cont" aria-hidden>
            Get one message
          </span>
        </h1>
      </div>

      {/* A mobile-only dashed beat — on narrow the massive headline gets its own
          screen-moment before the telemetry/schematic arrives (it's crowded at
          390px). Hidden ≥768px, where the headline already owns the top band. */}
      <TearRule inset mobileOnly />

      {/* The hero body — two columns under the headline: the plain sub-line + CTA
          on the left, the live transmission schematic (the one focal visual) on
          the right. A vertical dashed rule divides them. */}
      <div className="wt-hero__body">
        <div className="wt-hero__lede">
          <p className="wt-hero__sub">
            Your agents don&rsquo;t check in. One edit outside the plan &mdash;
            a migration, a force-push, a write to billing &mdash; costs you an
            afternoon. Waytide watches every session and reaches you only when
            the call is yours. The work never pauses.
          </p>
          <div className="wt-hero__cta">
            <AuthCta
              className="wt-receipt-button"
              outLabel="Sign in"
              inLabel="Dashboard"
            />
            <span className="wt-hero__cta-note">read-only to start</span>
          </div>
        </div>

        <div className="wt-hero__art">
          <ModeToggle mode={mode} setMode={setMode} />
          <Schematic mode={mode} />
        </div>
      </div>
    </section>
  );
}

/** The mode toggle — a segmented control `watching ⇄ caught`. The one real
 * interaction: flipping it re-renders the schematic from calm to alert.
 * role="radiogroup"; each segment a radio. The labels are the machine's state
 * words, so mono. */
function ModeToggle({
  mode,
  setMode,
}: {
  mode: Mode;
  setMode: (m: Mode) => void;
}) {
  return (
    <div
      className="wt-toggle"
      role="radiogroup"
      aria-label="Show the transmission watching, or catching an alert"
    >
      <span className="wt-toggle__lead" aria-hidden>
        state
      </span>
      {/* The seg carries a sliding thumb (the inverted fill) that TRAVELS
          left↔right between the two segments on change — directionally honest,
          not a cross-fade. data-on drives its position; the slide is gated under
          prefers-reduced-motion (reduced = instant). */}
      <div className="wt-toggle__seg" data-on={mode}>
        <span className="wt-toggle__thumb" aria-hidden />
        <button
          type="button"
          role="radio"
          aria-checked={mode === "watching"}
          className={`wt-toggle__opt${mode === "watching" ? " is-on" : ""}`}
          onClick={() => setMode("watching")}
          data-testid="landing-mode-watching"
        >
          watching
        </button>
        <span className="wt-toggle__sep" aria-hidden>
          &#x21C4;
        </span>
        <button
          type="button"
          role="radio"
          aria-checked={mode === "caught"}
          className={`wt-toggle__opt${mode === "caught" ? " is-on" : ""}`}
          onClick={() => setMode("caught")}
          data-testid="landing-mode-caught"
        >
          caught
        </button>
      </div>
    </div>
  );
}

/** THE SCHEMATIC — the live transmission: five cogs (Session Agent → Supervisor
 * → the Arbiters → Signal → Brain) as connected cards joined by dashed flow lines, a
 * pulse travelling them. Each cog shows a tiny mono status pill that differs
 * between calm/alert. Hover a cog → it highlights in its hue and reveals its
 * one-line role. In `caught`, the three operator cogs light and the alert runs
 * the flow. Corner registration ticks frame the card (the blueprint texture).
 * role="img" + an aria-label state the whole system as a sentence; the geometry is
 * aria-hidden. The cog names/numbers + pills are the machine record, so mono; the
 * label [ TRANSMISSION ] stays the mono tactical eyebrow. */
function Schematic({ mode }: { mode: Mode }) {
  const sentence =
    mode === "caught"
      ? "Caught: the Session Agent is running, the Supervisor caught an edit outside the agreed files, Engineering called it — a one-way door, worth a human — Signal sent one tap, and the Brain stays synced."
      : "Watching: the Session Agent is running, the Supervisor is watching, the arbiters are clear, Signal is quiet, and the Brain stays synced — nothing needs you.";

  return (
    <section
      className={`wt-receipt-schem is-${mode}`}
      aria-labelledby="wt-receipt-schem-h"
      role="img"
      aria-label={sentence}
    >
      <CornerTicks />
      <div className="wt-receipt-schem__head" aria-hidden>
        <span id="wt-receipt-schem-h" className="wt-eyebrow wt-eyebrow--solo">
          <span className="wt-eyebrow__tag">
            <span className="wt-eyebrow__bracket">[</span>
            <span className="wt-eyebrow__label">TRANSMISSION</span>
            <span className="wt-eyebrow__bracket">]</span>
          </span>
        </span>
        <span className={`wt-receipt-schem__state wt-receipt-schem__state--${mode}`}>
          {mode === "caught" ? "● caught" : "watching"}
        </span>
      </div>

      <ol className="wt-receipt-schem__flow" aria-hidden>
        {COGS.map((cog, i) => {
          const lit = mode === "caught" && cog.litInCaught;
          const status = mode === "caught" ? cog.caught : cog.calm;
          return (
            <li
              key={cog.id}
              className={[
                "wt-cog",
                cog.hue ? `wt-cog--${cog.hue}` : "",
                lit ? "is-lit" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              style={{ "--rcpt-cog-i": i } as React.CSSProperties}
              tabIndex={0}
            >
              <span className="wt-cog__edge" aria-hidden />
              <span className="wt-cog__top">
                <span className="wt-cog__name">{cog.name}</span>
              </span>
              <span
                className={`wt-cog__pill${lit ? " is-lit" : ""}`}
                data-status={status}
              >
                <span className="wt-cog__dot" aria-hidden />
                {status}
              </span>
              <span className="wt-cog__role">{cog.role}</span>
              {i < COGS.length - 1 ? (
                <span className="wt-cog__link" aria-hidden>
                  <span className="wt-cog__pulse" />
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>

      <p className="wt-receipt-schem__foot" aria-hidden>
        {mode === "caught"
          ? "one edit · caught → called → sent · agent never stopped"
          : "two doors · three arbiters · nothing needs you"}
      </p>
    </section>
  );
}

/** WHAT IT CAUGHT — one Alert, shown not narrated, in two columns. Left: ONE mono
 * summary line (① Supervisor caught → ② Engineering called → ③ Signal sent) as
 * three hue-marked chips, then the real diff (glyph AND hue). Right: the
 * email/steer card you'd actually receive — sans subject + body (a human wrote
 * it), three hard-edged action buttons, a mono provenance foot. The steer card
 * carries the page's single reserved amber (the "needs you" alert moment) and
 * corner registration ticks. A vertical rule divides what-happened from
 * what-you-get; a tactical eyebrow leads the band. */
function CatchBand() {
  return (
    <section className="wt-catch" aria-labelledby="wt-catch-h">
      <div className="wt-band__head">
        <Eyebrow index="01" label="WHAT IT CAUGHT" />
        <h2 id="wt-catch-h" className="wt-band__title">
          What it caught
        </h2>
        <p className="wt-band__deck">
          One real catch. The agent never stopped. You heard about the one edit
          that was yours to call.
        </p>
      </div>

      <div className="wt-catch__grid">
        <div className="wt-catch__flow">
          {/* The one mono summary line — three hue-marked operator chips. The
              operator-row classes ride the chips so the hue marks + the tests
              survive. (The "~1s" timing chip was dropped — we don't
              promise latency; an arbiter may consult an expert model and Signal
              times non-urgent contact to a seam, so a fixed number would be a claim
              we'd break.) */}
          <ol className="wt-receipt-catch" aria-label="Supervisor caught, Engineering called, Signal sent.">
            {CATCH_CHIPS.map((chip) => (
              <li
                key={chip.verb}
                className={`wt-receipt-catch__row wt-receipt-catch__row--${chip.op}`}
              >
                <span className="wt-receipt-catch__mark" aria-hidden />
                <span className="wt-receipt-catch__n" aria-hidden>
                  {chip.n}
                </span>
                <span className="wt-receipt-catch__op">{chip.name}</span>
                <span className="wt-receipt-catch__verb">{chip.verb}</span>
              </li>
            ))}
          </ol>

          {/* The real diff — glyph AND hue, never colour alone. The agreed file
              deleted-from (syntax-diff-del), the touched file added-to (add).
              File paths are the machine record → mono. Corner ticks frame it. */}
          <pre
            className="wt-receipt-diff"
            aria-label="The edit the Supervisor caught: a deletion in the agreed file and an addition in a file outside the agreed set."
          >
            <CornerTicks />
            <code>
              <span className="wt-receipt-diff__row wt-receipt-diff__row--del">
                <span className="wt-receipt-diff__glyph" aria-hidden>
                  &minus;
                </span>
                <span className="wt-receipt-diff__file">src/usage/export.ts</span>
                <span className="wt-receipt-diff__tag">agreed</span>
              </span>
              <span className="wt-receipt-diff__row wt-receipt-diff__row--add">
                <span className="wt-receipt-diff__glyph" aria-hidden>
                  +
                </span>
                <span className="wt-receipt-diff__file">src/billing/charge.ts</span>
                <span className="wt-receipt-diff__tag">touched</span>
              </span>
            </code>
          </pre>
        </div>

        {/* The email/steer card — the sentence to the human (sans), the three
            hard-edged action buttons, the mono provenance foot. The amber "needs
            you" stamp on the subject is the page's single reserved amber. Corner
            ticks frame the focal card. Re-ink on hover, dashed focus, no
            shadow/move. */}
        <div className="wt-catch__steer">
          {/* The steer card is the page's single reserved amber: a 2px amber
              left-edge tick (--waytide-comp-receipt-stamp) marks it as THE alert
              at a glance vs. the other outlined cards. The amber lives on this
              ONE element (wt-receipt-stamp) — no second amber dot. */}
          <div className="wt-receipt-steer wt-receipt-stamp">
            <CornerTicks />
            <p className="wt-receipt-steer__subject">
              Waytide &middot; an edit outside this run&rsquo;s plan
            </p>
            <p className="wt-receipt-steer__body">
              Supervisor caught a write to billing/charge.ts &mdash; outside this
              run&rsquo;s agreed files (src/usage/). Engineering called it: a one-way
              door, yours to decide. The agent&rsquo;s still running; nothing stopped.
            </p>
            <div className="wt-receipt-steer__actions">
              <button
                type="button"
                className="wt-receipt-action wt-receipt-action--primary"
              >
                Open session
              </button>
              <button type="button" className="wt-receipt-action">
                Let it proceed
              </button>
              <button type="button" className="wt-receipt-action">
                Pull it back
              </button>
            </div>
            <p className="wt-receipt-steer__foot">
              &mdash; Supervisor &middot; Engineering &middot; via Signal &middot; 14:02:10
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/** The trust band — a tactical eyebrow, then three ruled columns (read-only /
 * your runtime / zero retention), each a sans key + a terse sans clause (a human
 * wrote them), divided by vertical dashed rules. */
function TrustBand() {
  return (
    <section className="wt-trust" aria-labelledby="wt-trust-h">
      <div className="wt-band__head wt-band__head--quiet">
        <Eyebrow index="02" label="TRUST" />
        <h2 id="wt-trust-h" className="wt-band__title wt-band__title--sr">
          What Waytide does and does not do
        </h2>
      </div>
      <ul className="wt-trust__grid">
        {TRUST.map((c) => (
          <li key={c.k} className="wt-trust__cell">
            <span className="wt-trust__k">{c.k}</span>
            <span className="wt-trust__v">{c.v}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The close — a tactical eyebrow, then the one weighted affordance (sign in —
 * the Google/Apple choice lives on /signin) + a terse note, and the quiet
 * inline download, which reuses the
 * installer detection: a real link to the preferred installer, degrading to
 * "installer pending" in a local env. "all platforms ↓" routes to /download. No
 * slogan — declarative. */
function CloseBand() {
  const installers = DEFAULT_INSTALLERS;
  const preferred = selectPreferredInstaller(installers, readUserAgent());

  return (
    <section className="wt-close" aria-labelledby="wt-close-h">
      <div className="wt-band__head wt-band__head--quiet">
        <Eyebrow index="03" label="CONNECT" />
        <h2 id="wt-close-h" className="wt-band__title wt-band__title--sr">
          Sign in or download Waytide
        </h2>
      </div>
      <p className="wt-close__lede">
        Every run it learns &mdash; so it asks less and you run more. Start
        read-only, one session. Grant the rest when it&rsquo;s earned.
      </p>
      <div className="wt-close__act">
        <div className="wt-receipt-cta">
          <AuthCta
            className="wt-receipt-button wt-receipt-button--lg"
            testid="landing-signin-cta"
            outLabel="Sign in"
            inLabel="Dashboard"
          />
          <span className="wt-receipt-cta__note">Read-only to start. You grant the rest.</span>
        </div>

        <div className="wt-receipt-get">
          <span className="wt-receipt-get__lead">already have it?</span>
          <span className="wt-receipt-get__links">
            {preferred ? (
              <a
                href={preferred.href}
                className="wt-receipt-link"
                data-testid="desktop-download-primary"
              >
                download · {platformLabel(preferred.id)}
              </a>
            ) : (
              <span
                className="wt-receipt-link wt-receipt-link--pending"
                data-testid="desktop-download-primary"
              >
                installer pending
              </span>
            )}
            <span aria-hidden className="wt-receipt-get__sep">
              ·
            </span>
            <Link to="/download" className="wt-receipt-link">
              all platforms <span aria-hidden>&darr;</span>
            </Link>
          </span>
        </div>
      </div>
    </section>
  );
}

/** A reusable dashed tear-rule — a full-width SVG line on the receipt's dashed
 * cadence, the SEEN rule colour, non-scaling-stroke so the dash stays crisp at any
 * width. ONE line always (never a doubled hairline-pair): `heavy` is the band
 * divider — a SINGLE thick dashed line at the heavy weight (now 5px); the normal
 * rule is the 3px standard weight. The long, thin dashes (dasharray 30 5) read as
 * 90s-newspaper-coupon scissor cut-lines.
 * The dashes are FLAT (stroke-linecap: butt → square, rectangular dashes, no
 * rounding). `inset` pulls it inside a block. `mobileOnly` shows it only ≤767px
 * (the hero's narrow-screen breath beat). aria-hidden — purely structural. The SVG
 * box is tall enough to hold the centred 6px heavy stroke. */
function TearRule({
  heavy = false,
  inset = false,
  mobileOnly = false,
}: {
  heavy?: boolean;
  inset?: boolean;
  mobileOnly?: boolean;
}) {
  const cls = [
    "wt-receipt-rule",
    heavy ? "wt-receipt-rule--heavy" : "",
    inset ? "wt-receipt-rule--inset" : "",
    mobileOnly ? "wt-receipt-rule--mobile-only" : "",
  ]
    .filter(Boolean)
    .join(" ");
  // One thick flat dashed line, centred in the box. Heavy = the 5px band divider;
  // normal = the 3px standard rule. An 8px-tall box centres the heaviest (5px)
  // stroke at y=4 without clipping.
  const box = 8;
  const mid = box / 2;
  return (
    <svg
      className={cls}
      width="100%"
      height={box}
      preserveAspectRatio="none"
      aria-hidden
      focusable="false"
    >
      <line
        x1="0"
        y1={mid}
        x2="100%"
        y2={mid}
        stroke="var(--waytide-comp-receipt-rule)"
        strokeDasharray="var(--waytide-comp-receipt-dash)"
        strokeWidth={
          heavy
            ? "var(--waytide-comp-receipt-rule-heavy)"
            : "var(--waytide-comp-receipt-rule-width)"
        }
        strokeLinecap="butt"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
